import { Capacitor } from '@capacitor/core';
import { captureException } from '@sentry/react';
import { useMemo, useState } from 'react';
import { useAsyncRetry, useUpdateEffect } from 'react-use';

export interface RoutesImporterState {
  loading: boolean;
  routes:
    | typeof import('./routes-native')
    | typeof import('./routes-web')
    | undefined;
}

const loadRoutes = () => {
  if (Capacitor.isNativePlatform()) {
    return import('./routes-native');
  } else {
    return import('./routes-web');
  }
};

export const useRoutesImporter = (): RoutesImporterState => {
  const [hasReportedError, setHasReportedError] = useState(false);
  const { retry, value, error, loading } = useAsyncRetry(async () =>
    loadRoutes(),
  );

  useUpdateEffect(() => {
    if (!error || value) {
      return;
    }

    if (!hasReportedError) {
      // Avoid spamming errors
      captureException(error);
      setHasReportedError(true);
    }

    // Retry loading the routes until it works since app
    // will be unusable if routes are not loaded
    setTimeout(retry, 500);
  }, [error, loading]);

  return useMemo(
    (): RoutesImporterState => ({
      loading,
      routes: value,
    }),
    [loading, value],
  );
};
